import {
  MeshStandardMaterial,
  DoubleSide,
  Mesh,
  Box3,
  Group,
  Vector3,
  Box3Helper,
  Matrix4,
  Line3,
  Raycaster,
  Intersection,
  Sphere,
} from "three";
import { MeshBVH, MeshBVHVisualizer } from "three-mesh-bvh";
import { Capsule } from "three/examples/jsm/math/Capsule";
import { GLTF } from "three/examples/jsm/loaders/GLTFLoader.js";

export class Collider {
  private box: Box3 = new Box3();

  collider: Mesh;

  helper: MeshBVHVisualizer;
  boundary: Box3Helper;

  private bvh: MeshBVH;

  private raycaster: Raycaster;

  private invMatWorld = new Matrix4();

  private queryBox = new Box3();
  private triPoint = new Vector3();
  private capsulePoint = new Vector3();
  private tempSegment = new Line3();

  private constructor(group: Group, colliderMesh: Mesh) {
    this.box = new Box3().setFromObject(group);
    // TODO: work out why we need these bodges
    this.box.expandByScalar(1.2);
    this.box.min.y = -100;
    this.box.max.y = 800;
    this.box.min.x *= 1.5;
    this.box.max.x *= 1.5;
    this.box.min.z *= 1.5;
    this.box.max.z *= 1.5;

    const geometry = colliderMesh.geometry;
    this.bvh = geometry.boundsTree = new MeshBVH(geometry);

    this.collider = colliderMesh;
    this.collider.visible = false;
    this.collider.material = new MeshStandardMaterial({
      color: "blue",
      wireframe: true,
      side: DoubleSide,
    });

    this.invMatWorld.copy(this.collider.matrixWorld).invert();

    this.helper = new MeshBVHVisualizer(this.collider, 10);
    this.helper.visible = false;

    this.boundary = new Box3Helper(this.box);
    this.boundary.visible = false;

    group.add(this.collider);
    group.add(this.helper);
    group.add(this.boundary);

    this.raycaster = new Raycaster();
    this.raycaster.firstHitOnly = true;
  }

  inBounds(pos: Vector3): boolean {
    return this.box.containsPoint(pos);
  }

  raycast(origin: Vector3, dir: Vector3): Intersection | undefined {
    this.raycaster.set(origin, dir);
    const intersections = this.raycaster.intersectObject(this.collider);
    return intersections.length > 0 ? intersections[0] : undefined;
  }

  collideSphere(position: Vector3, radius: number): boolean {
    return this.bvh.intersectsSphere(new Sphere(position, radius));
  }

  collideCapsule(capsule: Capsule, penetration?: Vector3): boolean {
    this.tempSegment.start.copy(capsule.start);
    this.tempSegment.end.copy(capsule.end);

    // get position in local space of collider
    this.tempSegment.start.applyMatrix4(this.invMatWorld);
    this.tempSegment.end.applyMatrix4(this.invMatWorld);

    this.queryBox.makeEmpty();
    this.queryBox.expandByPoint(this.tempSegment.start);
    this.queryBox.expandByPoint(this.tempSegment.end);
    this.queryBox.min.addScalar(-capsule.radius);
    this.queryBox.max.addScalar(capsule.radius);

    let collided = false;

    this.bvh.shapecast({
      intersectsBounds: (box) => box.intersectsBox(this.queryBox),
      intersectsTriangle: (tri) => {
        const distance = tri.closestPointToSegment(
          this.tempSegment,
          this.triPoint,
          this.capsulePoint
        );

        if (distance < capsule.radius) {
          if (penetration) {
            const depth = capsule.radius - distance;
            penetration
              .subVectors(this.capsulePoint, this.triPoint)
              .normalize()
              .multiplyScalar(depth);

            penetration.applyMatrix4(this.collider.matrixWorld);
          }
          collided = true;
        }
      },
    });

    return collided;
  }

  static fromGLTF(gltf: GLTF): Collider | undefined {
    const colliderMesh = gltf.scene.getObjectByName("CameraCollider");
    if (colliderMesh && colliderMesh instanceof Mesh) {
      colliderMesh.visible = false;
      colliderMesh.receiveShadow = false;
      colliderMesh.castShadow = false;
      return new Collider(gltf.scene, colliderMesh);
    } else {
      return undefined;
    }
  }
}
